import type { ComponentProps } from 'react';
import type { WrapperFC } from '@george-labs.com/design-system';
import type { COLOR } from '@george-labs.com/design-system/types/utilsTS/color';
import { Avatar } from '@george-labs.com/design-system';
import { useMemo } from 'react';

import type { ProductWithHelpers } from '@shared/products-core';
import { getCustomAvatarProperties } from '@shared/core-ui-avatar';
import { useGlobalColor } from '@shared/core-ui-layout';

type Props = {
    product: ProductWithHelpers;
    hasColorPreview?: true;
    size?: `${ComponentProps<typeof Avatar>['size']}`;
    'data-cy'?: string;
    className?: string;
};

export const ProductAvatar: WrapperFC<Props> & {
    COLOR: typeof COLOR;
} = ({
    product,
    'data-cy': dataCy = 'product-avatar',
    size,
    hasColorPreview,
    ...passThroughProps
}) => {
    const { value: globalColor } = useGlobalColor();
    const productImage = product.imageData ?? product.alternativeImageUrl;
    const { backgroundColor, color, variant } = useMemo(() => {
        if (!hasColorPreview) {
            return getCustomAvatarProperties(product.additionalData?.color);
        }

        return getCustomAvatarProperties(globalColor);
    }, [globalColor, product.additionalData?.color]);

    const avatarBackground = productImage ? Avatar.COLOR.SURFACE_SECONDARY : backgroundColor;

    const flags = [...(product.underlyingCard?.flags || []), ...(product.flags || [])];

    const insuredProductBadge: Pick<
        ComponentProps<typeof Avatar>,
        'badgeIcon' | 'badgeColor'
    > = flags.includes('insuredProduct')
        ? { badgeIcon: Avatar.ICON.INSURANCE, badgeColor: Avatar.COLOR.UI_BLUE }
        : {};

    return (
        <Avatar
            icon={product.icon}
            imageUrl={productImage}
            color={color}
            backgroundColor={avatarBackground}
            variant={variant}
            data-cy={dataCy}
            size={size as ComponentProps<typeof Avatar>['size']}
            {...insuredProductBadge}
            {...passThroughProps}
        />
    );
};

ProductAvatar.wrapsComponent = Avatar;
ProductAvatar.COLOR = Avatar.COLOR;
